import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './Calendario.css';
import './Temporadas/Temporadas.css';
import { useTemporada } from '../context/TemporadaContext';
import { useAuth } from '../context/AuthContext';
import {
  getEventosCalendarioReal,
  crearEventoCalendarioReal,
  actualizarEventoCalendarioReal,
  borrarEventoCalendarioReal,
} from '../services/calendarioService';
import {
  googleCalendarConfigurado,
  getEventosGoogleCalendar,
  urlSuscripcionGoogle,
  urlSuscripcionIcal,
} from '../services/googleCalendarService';
import { anioInicioTemporada, generarDomingosTemporada, construirFilasCalendario, TIPOS_EVENTO } from '../utils/calendarioUtils';

const FORM_VACIO = { fechaInicio: '', fechaFin: '', tipo: 'AVISO', titulo: '', descripcion: '' };

function Calendario() {
  const location = useLocation();
  const esPrivado = location.pathname.startsWith('/privado');
  const { temporada } = useTemporada();
  const { usuario } = useAuth();
  const esAdmin = esPrivado && (usuario?.rol === 'ADMIN' || usuario?.rol === 'COMITE');

  const [eventos, setEventos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [eventosGoogle, setEventosGoogle] = useState([]);
  const [errorGoogle, setErrorGoogle] = useState(false);

  const [form, setForm] = useState(FORM_VACIO);
  const [editandoId, setEditandoId] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);

  const recargar = () => temporada && getEventosCalendarioReal(temporada).then(setEventos);

  useEffect(() => {
    if (!temporada) return;
    setCargando(true);
    setError(null);
    getEventosCalendarioReal(temporada)
      .then(setEventos)
      .catch(() => setError('No se pudo cargar el calendario. ¿Está el servidor en marcha?'))
      .finally(() => setCargando(false));
  }, [temporada]);

  // Eventos del calendario de Google: se consultan al entrar (y al cambiar de
  // temporada), así que los nuevos aparecen sin tocar nada aquí. Si falla, la
  // tabla sigue mostrando los eventos de la temporada.
  useEffect(() => {
    if (!temporada || !googleCalendarConfigurado) return;
    const anio = anioInicioTemporada(temporada);
    setErrorGoogle(false);
    getEventosGoogleCalendar(`${anio}-09-01`, `${anio + 1}-06-30`)
      .then(setEventosGoogle)
      .catch(() => {
        setEventosGoogle([]);
        setErrorGoogle(true);
      });
  }, [temporada]);

  const domingos = useMemo(
    () => (temporada ? generarDomingosTemporada(anioInicioTemporada(temporada)) : []),
    [temporada],
  );
  const filas = useMemo(
    () => construirFilasCalendario(domingos, [...eventos, ...eventosGoogle]),
    [domingos, eventos, eventosGoogle],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorForm(null);

    const datos = {
      fechaInicio: form.fechaInicio,
      fechaFin: form.fechaFin || form.fechaInicio,
      tipo: form.tipo,
      titulo: form.titulo.trim(),
      descripcion: form.descripcion.trim() || undefined,
    };
    if (!datos.fechaInicio || !datos.titulo) {
      setErrorForm('Rellena al menos la fecha de inicio y el título.');
      return;
    }

    setGuardando(true);
    const guardar = editandoId
      ? actualizarEventoCalendarioReal(editandoId, datos)
      : crearEventoCalendarioReal(temporada, datos);

    guardar
      .then(() => {
        cerrarModal();
        return recargar();
      })
      .catch((err) => setErrorForm(err.message || 'No se pudo guardar.'))
      .finally(() => setGuardando(false));
  };

  // Precarga un evento ya existente, incluida la fecha (se puede cambiar en
  // el propio formulario) para editarlo.
  const handleEditarEvento = (evento) => {
    setErrorForm(null);
    setEditandoId(evento.id);
    setForm({
      fechaInicio: evento.fechaInicio,
      fechaFin: evento.fechaFin,
      tipo: evento.tipo,
      titulo: evento.titulo,
      descripcion: evento.descripcion ?? '',
    });
    setModalAbierto(true);
  };

  // Fila en blanco (un domingo sin evento todavía): precarga solo su fecha,
  // para no tener que rellenarla a mano al crear uno nuevo.
  const handleCrearEnFecha = (fecha) => {
    setErrorForm(null);
    setEditandoId(null);
    setForm({ ...FORM_VACIO, fechaInicio: fecha, fechaFin: fecha });
    setModalAbierto(true);
  };

  const handleAnadir = () => {
    setErrorForm(null);
    setEditandoId(null);
    setForm(FORM_VACIO);
    setModalAbierto(true);
  };

  function cerrarModal() {
    setModalAbierto(false);
    setEditandoId(null);
    setForm(FORM_VACIO);
    setErrorForm(null);
  }

  const handleBorrar = (id) => {
    setErrorForm(null);
    borrarEventoCalendarioReal(id).then(recargar).catch((err) => setErrorForm(err.message || 'No se pudo borrar.'));
  };

  return (
    <div className="calendario-page">
      <div className="calendario-cabecera">
        <h2 className="calendario-titulo">Calendario {temporada ?? ''}</h2>
        {esAdmin && (
          <button type="button" className="btn btn-primary" onClick={handleAnadir}>Añadir</button>
        )}
      </div>
      <p className="calendario-subtitulo">Temporada de septiembre a junio, con todos los domingos.</p>

      {googleCalendarConfigurado && (
        <p className="calendario-suscripcion">
          <a className="btn btn-secondary btn-sm" href={urlSuscripcionGoogle()} target="_blank" rel="noopener noreferrer">
            Suscribirme
          </a>
          <a href={urlSuscripcionIcal()}>Apple / Outlook (iCal)</a>
        </p>
      )}
      {errorGoogle && (
        <p className="calendario-mensaje msg-error">No se pudieron cargar los eventos del calendario de Google.</p>
      )}

      {cargando && <p className="calendario-mensaje">Cargando...</p>}
      {error && <p className="calendario-mensaje msg-error">{error}</p>}

      {!cargando && !error && (
        <div className="calendario-tabla-scroll">
          <table className="calendario-tabla">
            <thead>
              <tr>
                <th className="cal-col-dia">Día</th>
                <th className="cal-col-mes">Mes</th>
                <th className="cal-col-anio">Año</th>
                <th className="cal-col-fecha">Fecha</th>
                <th>Evento</th>
                {esAdmin && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filas.map((fila) => (
                <tr
                  key={fila.key}
                  className={[
                    fila.eventos[0] ? TIPOS_EVENTO[fila.eventos[0].tipo]?.clase : 'cal-fila-domingo',
                    fila.pasada && 'cal-fila-pasada',
                    fila.proxima && 'cal-fila-proxima',
                  ].filter(Boolean).join(' ')}
                >
                  <td className="cal-col-dia">{fila.dia}</td>
                  <td className="cal-col-mes">{fila.mes}</td>
                  <td className="cal-col-anio">{fila.anio}</td>
                  <td className="cal-col-fecha">{fila.fechaCorta}</td>
                  <td>
                    {fila.eventos.map((evento) => (
                      <div key={evento.id} className="cal-evento-bloque">
                        <span className="cal-evento-titulo">{evento.titulo}</span>
                        {evento.descripcion && <span className="cal-evento-descripcion">{evento.descripcion}</span>}
                      </div>
                    ))}
                  </td>
                  {esAdmin && (
                    <td className="cal-acciones">
                      {fila.eventos.length === 0 ? (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleCrearEnFecha(fila.domingo)}>Editar</button>
                      ) : (
                        fila.eventos.map((evento) => (
                          <div key={evento.id} className="cal-acciones-fila">
                            {!evento.externo && (
                              <>
                                <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleEditarEvento(evento)}>Editar</button>
                                <button type="button" className="btn btn-danger btn-sm" onClick={() => handleBorrar(evento.id)}>Borrar</button>
                              </>
                            )}
                          </div>
                        ))
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {esAdmin && modalAbierto && (
        <div
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && !guardando && cerrarModal()}
        >
          <div className="modal-box">
          <h3>{editandoId ? 'Editar evento' : 'Añadir festivo, evento o aviso'}</h3>
          <form className="calendario-form" onSubmit={handleSubmit}>
            <label>
              Desde
              <input
                type="date"
                value={form.fechaInicio}
                onChange={(e) => setForm({ ...form, fechaInicio: e.target.value })}
              />
            </label>
            <label>
              Hasta
              <input
                type="date"
                value={form.fechaFin}
                onChange={(e) => setForm({ ...form, fechaFin: e.target.value })}
              />
            </label>
            <label>
              Tipo
              <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                {Object.entries(TIPOS_EVENTO).map(([valor, { etiqueta }]) => (
                  <option key={valor} value={valor}>{etiqueta}</option>
                ))}
              </select>
            </label>
            <label className="calendario-form-titulo">
              Título
              <input
                placeholder="Ej. Puente de la Constitución"
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              />
            </label>
            <label className="calendario-form-descripcion">
              Descripción (opcional)
              <input
                placeholder="Ej. Días 7 y 8 de diciembre, festivos"
                value={form.descripcion}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              />
            </label>
            <div className="calendario-form-botones">
              <button type="submit" className="btn btn-primary" disabled={guardando}>
                {editandoId ? 'Guardar cambios' : 'Añadir'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={cerrarModal} disabled={guardando}>Cancelar</button>
            </div>
          </form>
          {errorForm && <p className="msg-error">{errorForm}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default Calendario;
